import type { CityConfig } from "../city-template/types";

export const fortCollinsConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Fort Collins",
  state: "Colorado",
  stateAbbr: "CO",
  slug: "fort-collins",
  route: "/fort-collins",
  title: "Emergency Passport Fort Collins, CO — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Fort Collins, CO — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Larimer County and northern Colorado travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Fort Collins, CO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Fort Collins and northern Colorado travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Fort%20Collins%20Colorado%20downtown%20with%20historic%20Old%20Town%20buildings%2C%20Colorado%20State%20University%20campus%20visible%2C%20Cache%20la%20Poudre%20River%20nearby%2C%20warm%20golden%20hour%20light%2C%20modern%20and%20historic%20architecture%20blend%2C%20tree-lined%20streets%20with%20mountain%20foothills%20in%20background%2C%20clean%20and%20scenic%20college%20town%20atmosphere%2C%20professional%20cityscape%20photography%2C%20warm%20amber%20tones%2C%20no%20visible%20text%20or%20people%2C%20cinematic%20wide%20angle%20view&width=1800&height=700&seq=fcollins-hero-001&orientation=landscape",
  heroImageAlt: "Fort Collins Colorado downtown with historic Old Town and Colorado State University",
  geoPosition: "40.5853;-105.0844",
  geoPlacename: "Fort Collins, Colorado",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Colorado Passport Agency",
    address: "7245 S Havana Street Suite B-600, Centennial, CO 80112",
    distance: "Approximately 65 miles south — about 1 hour via I-25",
  },
  nearbyCitiesZips: [
    { name: "Old Town Fort Collins", zips: "80524" },
    { name: "Midtown", zips: "80525" },
    { name: "South Fort Collins", zips: "80526" },
    { name: "Horsetooth", zips: "80526" },
    { name: "CSU Campus", zips: "80523" },
    { name: "Loveland", zips: "80537–80539" },
    { name: "Windsor", zips: "80550" },
    { name: "Greeley", zips: "80631–80634" },
    { name: "Timnath", zips: "80547" },
    { name: "Wellington", zips: "80549" },
    { name: "Berthoud", zips: "80513" },
    { name: "Estes Park", zips: "80517" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Fort Collins departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Denver", route: "/denver" },
    { name: "Emergency passport help in Colorado Springs", route: "/colorado-springs" },
    { name: "Urgent passport services in Boulder", route: "/boulder" },
    { name: "Same-day passport help in Loveland", route: "/fort-collins" },
    { name: "Expedited passport services in Greeley", route: "/fort-collins" },
    { name: "Passport courier services in Windsor", route: "/fort-collins" },
    { name: "Rush passport help in Estes Park", route: "/fort-collins" },
  ],
  fpcOfficeAddress: "7245 S Havana Street Suite B-600, Centennial, CO 80112",
  fpcOfficeNote: "Fast Passport Center serves Fort Collins remotely via secure courier. We do not have a walk-in office in Fort Collins — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Fort Collins travelers.",
  fpcOfficeDistance: "Serving all of Larimer County and northern Colorado",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Larimer County and northern Colorado. From Old Town Fort Collins to the mountain gateway communities, from the university to the agricultural corridor — we help Fort Collins travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Fort Collins neighborhoods and nearby communities including Loveland, Windsor, Greeley, Timnath, and Wellington. If you are in Larimer County or northern Colorado, we have you covered.",
  howItWorksIntro: "Most Fort Collins travelers complete everything online and ship documents overnight. No need to drive to Denver or wait in line at the regional agency. Our emergency and expedited passport process is designed for Fort Collins travelers who need fast, reliable results.",
  mistakesIntro: "With Colorado State University sending students abroad and Fort Collins professionals traveling internationally, northern Colorado travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Fort Collins travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Fort Collins? Here are the most common ones we hear from northern Colorado travelers.",
  statePageRoute: "/states/colorado",
  statePageName: "Colorado",
  dmvWebsite: "https://dmv.colorado.gov",
  dmvLabel: "Colorado DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Fort Collins international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Alex T.",
      location: "CSU Student, Fort Collins",
      rating: 5,
      text: "Study abroad to Australia started in 4 days and my passport was expired. Fast Passport Center got me a new one in 48 hours. Made my program start date.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer K.",
      location: "Old Town, Fort Collins",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was smooth, they reviewed everything, and I had my passport in 5 days.",
      service: "Passport Renewal",
    },
    {
      name: "Robert M.",
      location: "Windsor, CO",
      rating: 5,
      text: "Lost my passport before a family trip to Italy. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Dr. Patricia L.",
      location: "Midtown, Fort Collins",
      rating: 5,
      text: "As a researcher who travels internationally for conferences, I can't afford delays. Fast Passport Center has been my go-to for years. Always reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "fc-1",
      name: "Fort Collins Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "301 E. Boardwalk Dr",
      city: "Fort Collins",
      state: "CO",
      zip: "80525",
      phone: "(970) 223-5374",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Foothills Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "fc-2",
      name: "Old Town Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "151 N. College Ave",
      city: "Fort Collins",
      state: "CO",
      zip: "80524",
      phone: "(970) 482-5264",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Old Town Square",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "fc-3",
      name: "Loveland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "446 E. 29th St",
      city: "Loveland",
      state: "CO",
      zip: "80538",
      phone: "(970) 667-5453",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Loveland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Loveland and south Fort Collins residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "fc-4",
      name: "Greeley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "930 9th Ave",
      city: "Greeley",
      state: "CO",
      zip: "80631",
      phone: "(970) 353-0630",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Greeley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "fc-5",
      name: "Windsor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "215 N. 7th St",
      city: "Windsor",
      state: "CO",
      zip: "80550",
      phone: "(970) 686-7424",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Windsor downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Windsor and Severance residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "fc-6",
      name: "Larimer County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "200 W. Oak St",
      city: "Fort Collins",
      state: "CO",
      zip: "80521",
      phone: "(970) 498-7860",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from Old Town Fort Collins",
      website: "https://www.larimer.gov/",
      websiteLabel: "Larimer County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Denver (65 mi)", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Fort Collins photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Fort Collins acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Fort Collins applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Fort Collins families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Fort Collins travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Fort Collins." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Fort Collins address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Fort Collins?",
      a: "Fast Passport Center offers emergency passport processing in Fort Collins in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Fort Collins travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Fort Collins?",
      a: "Same-day passports are not available in Fort Collins. The closest regional passport agency is in Denver (65 miles south). For urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Fort Collins residents without making the drive to Denver.",
    },
    {
      q: "Where can I get a fast passport renewal in Fort Collins?",
      a: "Fast Passport Center offers fast passport renewal in Fort Collins with no office visit required. We serve all neighborhoods including Old Town, Midtown, South Fort Collins, and the CSU campus area. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Fort Collins?",
      a: "The fastest way to get an urgent passport in Fort Collins is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "How fast can I get a passport in Fort Collins?",
      a: "With Fast Passport Center's emergency courier service, Fort Collins travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Colorado Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight from Fort Collins. Our couriers handle the in-person delivery to State Department facilities. No need to make the 65-mile drive to Denver.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Fort Collins?",
      a: "Popular options include the Fort Collins Main Post Office (80525), Old Town Post Office (80524), Loveland Post Office (80538), and Larimer County Clerk (80521). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in a nearby town like Loveland or Greeley?",
      a: "We serve all of Larimer County and surrounding communities including Loveland, Windsor, Greeley, Timnath, and Wellington. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Fort Collins?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Fort Collins?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Fort Collins offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Fort Collins?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Fort Collins?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Fort Collins families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — College Ave", address: "2001 S. College Ave, Fort Collins, CO 80525", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walgreens — Harmony", address: "4501 S. College Ave, Fort Collins, CO 80525", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/storelocator" },
    { name: "FedEx Office — Midtown", address: "1631 S. College Ave, Fort Collins, CO 80525", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/en-us/location.html" },
    { name: "UPS Store — Old Town", address: "119 N. College Ave, Fort Collins, CO 80524", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "Walmart — Loveland", address: "1325 Denver Ave, Loveland, CO 80538", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "Rite Aid — Greeley", address: "2620 11th Ave, Greeley, CO 80631", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/locations" },
    { name: "Postal Annex — Windsor", address: "1295 Main St, Windsor, CO 80550", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/locations" },
    { name: "Costco — Fort Collins", address: "4801 S. College Ave, Fort Collins, CO 80525", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/warehouse-locations" },
  ],
  dmvLocations: [
    {
      name: "DMV — Fort Collins",
      address: "3030 S. College Ave, Fort Collins, CO 80525",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Loveland",
      address: "127 E. 6th St, Loveland, CO 80537",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Greeley",
      address: "3130 11th Ave, Greeley, CO 80631",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Windsor",
      address: "100 N. 7th St, Windsor, CO 80550",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Weld County north. Extended hours available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "DEN",
      name: "Denver International Airport",
      distance: "65 miles from Fort Collins via I-25",
      address: "8500 Peña Blvd, Denver, CO 80249",
      note: "The primary international airport for northern Colorado travelers. Groome Transportation shuttle service available from Fort Collins.",
      flightUrl: "https://www.flydenver.com/",
      flightLabel: "DEN Flight Info",
      featured: true,
    },
    {
      code: "COS",
      name: "Colorado Springs Airport",
      distance: "140 miles from Fort Collins",
      address: "7770 Milton E. Proby Pkwy, Colorado Springs, CO 80916",
      note: "Regional airport alternative for southern Colorado connections.",
      flightUrl: "https://www.coloradosprings.gov/airport",
      flightLabel: "Colorado Springs Airport",
      featured: false,
    },
  ],
};